import { Button } from '@ttab/elephant-ui'
import { PlusIcon, ChevronDownIcon, type LucideIcon } from '@ttab/elephant-ui/icons'
import * as Views from '@/views'
import { getTemplateFromView } from '@/shared/templates/lib/getTemplateFromView'
import { cn } from '@ttab/elephant-ui/utils'
import type { View } from '@/types/index'
import { useModal } from '../Modal/useModal'
import {
  ButtonGroup,
  ButtonGroupSeparator,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@ttab/elephant-ui'
import { addButtonGroupValueFormat } from '@/defaults/documentTypeFormats'
import type { buttonVariants } from '@ttab/elephant-ui'
import type { VariantProps } from 'class-variance-authority'
import type { QueryParams } from '@/hooks/useQuery'
import { useLink } from '@/hooks/useLink'
import { useSession } from 'next-auth/react'
import { createNewFactbox } from './lib/createNewFactbox'
import { toast } from 'sonner'
import { useRegistry } from '@/hooks/useRegistry'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useDocumentDefaults } from '@/hooks/useDocumentDefaults'
import { TimelessCreation } from '@/views/TimelessCreation'
import { useOpenMemoCreation } from '@/views/MemoCreation/hooks/useOpenMemoCreation'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'

const addButtonTypes = ['core/planning-item', 'core/event', 'core/article', 'core/factbox', 'core/flash', 'core/article#timeless', 'tt/memo'] as const

type Variant = VariantProps<typeof buttonVariants>['variant']
type ButtonView = { name: View, type: keyof typeof addButtonGroupValueFormat, icon?: { icon?: LucideIcon, color?: string } }

const getViewLabel = (view: ButtonView, hast?: boolean): string => {
  if (view.name === 'Flash' && hast) {
    return 'HAST'
  }

  return addButtonGroupValueFormat[view.type]?.label ?? ''
}

const AddButton = ({
  variant = 'default',
  className,
  view,
  onClick,
  t
}: {
  variant?: Variant
  className?: string
  view: ButtonView
  onClick: (view: ButtonView) => void
  t: TFunction
}) => {
  return (
    <Button
      size='sm'
      variant={variant}
      className={cn('h-8 pr-4', className)}
      onClick={() => onClick(view)}
    >
      <PlusIcon size={18} strokeWidth={1.75} />
      <span className='pl-0.5'>{t('common:misc.new')}</span>
    </Button>
  )
}

export const AddButtonGroup = ({ docType = 'core/planning-item', query }: { type: View, query: QueryParams, docType?: string }) => {
  const { showModal, hideModal } = useModal()
  const { repository, featureFlags } = useRegistry()
  const openFactboxEditor = useLink('Factbox')
  const openEditor = useLink('Editor')
  const { data: session } = useSession()
  const { t } = useTranslation()
  const hasHast = !!featureFlags.hasHast
  const { hasMemoCreation } = useFeatureFlags(['hasMemoCreation'])
  const defaults = useDocumentDefaults()
  const openMemoCreation = useOpenMemoCreation()

  const views: ButtonView[] = addButtonTypes
    .filter((type) => {
      // Memo creation ships dark until a deployment opts in.
      if (type === 'tt/memo') {
        return !!hasMemoCreation
      }

      return true
    })
    .map((type) => {
      const format = addButtonGroupValueFormat[type]
      return { name: format.key as View, type, icon: { icon: format.icon, color: format.color, label: format.label } }
    })

  const firstItem = views.find((view) => view.type === docType) as ButtonView
  const ItemIcon = firstItem.icon

  const handleCreate = (view: ButtonView) => {
    const ViewDialog = Views[view.name]
    const id = crypto.randomUUID()

    if (view.name === 'Factbox') {
      createNewFactbox(repository, session, id)
        .then((id) => openFactboxEditor(undefined, { id }, undefined))
        .catch((error: unknown) => {
          console.error('Error creating factbox:', error)
          toast.error((error as Error).message)
        })
      return
    }

    if (view.type === 'tt/memo') {
      openMemoCreation(query)
    } else if (view.type === 'core/article#timeless' && showModal) {
      showModal(
        <TimelessCreation
          id={id}
          onClose={(createdId) => {
            hideModal()
            if (createdId) {
              openEditor(undefined, { id: createdId }, undefined)
            }
          }}
        />
      )
    } else if (showModal) {
      const initialDocument = getTemplateFromView(view.name, { useHast: hasHast })(id, { ...defaults, query })
      showModal(
        <ViewDialog
          onDialogClose={hideModal}
          asDialog
          id={id}
          document={initialDocument}
        />
      )
    }
  }

  return (
    <ButtonGroup>
      <AddButton
        t={t}
        view={firstItem?.type ? firstItem : views[0]}
        onClick={handleCreate}
      />
      <ButtonGroupSeparator />
      <DropdownMenu>
        <div>
          <DropdownMenuTrigger
            nativeButton={false}
            className='h-full rounded-br-md rounded-tr-md bg-primary hover:bg-primary/90 cursor-pointer transition-all'
            render={(
              <div className='px-1.5'>
                <ChevronDownIcon size={14} strokeWidth={1.75} className='self-center h-full text-white dark:text-black' />
              </div>
            )}
          />
        </div>
        <DropdownMenuContent>
          {firstItem?.type && (
            <DropdownMenuItem
              inset={false}
              className='py-1.5 px-2 cursor-pointer'
              onClick={() => handleCreate(firstItem)}
            >
              {ItemIcon?.icon && <ItemIcon.icon strokeWidth={1.75} size={18} color={ItemIcon.color} />}
              <span className='pl-4'>{getViewLabel(firstItem, hasHast)}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {views.filter((view) => view.type !== docType).map((view) => {
            const ViewIcon = view.icon

            return (
              <DropdownMenuItem
                inset={false}
                className='py-1.5 px-2 cursor-pointer'
                key={view.name}
                onClick={() => handleCreate(view)}
              >
                {ViewIcon?.icon && <ViewIcon.icon strokeWidth={1.75} size={18} color={ViewIcon.color} />}
                <span className='pl-4'>{getViewLabel(view, hasHast)}</span>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </ButtonGroup>
  )
}
